import React from 'react';
import styled from 'styled-components';
import { Flex, Tabs, Tab, Tag } from '@decisiv/ui-components';
import { AdminPage, AdminPanel } from 'lib/admin-kit';
import PatchedTable from 'components/PatchedTable';

const SUMMARY_FIELDS = (labels) => [
  [labels.origin, 'origin'],
  [labels.triggeredAt, 'triggered_at'],
  [labels.sourceAccount, 'source_account'],
  [labels.createdAt, 'created_at'],
  [labels.vin, 'vin'],
  [labels.deviceId, 'device_id'],
  [labels.serialNumber, 'serial_number'],
  [labels.chassisId, 'chassis_id'],
];

const SummaryGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 16px 24px;
`;

const Label = styled.div`
  font-size: 12px;
  font-weight: 600;
  color: #555;
  margin-bottom: 4px;
`;

const Value = styled.div`
  font-size: 14px;
  color: #1a202c;
`;

const ENDPOINT = '/internal_admin/admin_kit/diagnostic_readings';
const FAULTS_ENDPOINT = '/internal_admin/admin_kit/faults';

const renderText = (value) => (value == null || value === '' ? '—' : String(value));

const FaultsTable = ({ faults, labels }) => {
  if (!faults?.length) {
    return (
      <Flex padding={2} style={{ background: '#f4f5f7', borderRadius: 4 }}>
        <span>{labels.noFaults}</span>
      </Flex>
    );
  }

  const columns = [
    { name: 'fault_code', title: labels.faultCode, DataCell: ({ rowData }) => <span>{renderText(rowData.fault_code)}</span> },
    { name: 'spn', title: 'SPN', DataCell: ({ rowData }) => <span>{renderText(rowData.spn)}</span> },
    { name: 'fmi', title: 'FMI', DataCell: ({ rowData }) => <span>{renderText(rowData.fmi)}</span> },
    { name: 'severity', title: labels.severity, DataCell: ({ rowData }) => <span>{renderText(rowData.severity)}</span> },
    { name: 'triggered_at', title: labels.triggeredAt, DataCell: ({ rowData }) => <span>{renderText(rowData.triggered_at)}</span> },
    {
      name: 'status',
      title: labels.status,
      DataCell: ({ rowData }) => (
        rowData.active
          ? <Tag intent="danger" text={labels.active} />
          : <Tag intent="neutral" text={labels.inactive} />
      ),
    },
    {
      name: 'view',
      title: '',
      DataCell: ({ rowData }) => (
        <a href={`${FAULTS_ENDPOINT}/${rowData.id}`} style={{ color: '#1a73c8', textDecoration: 'underline' }}>
          {labels.view}
        </a>
      ),
    },
  ];

  return <PatchedTable columns={columns} data={faults} getRowKey={(r) => r.id} />;
};

const SensorReadingsTable = ({ rows, labels }) => {
  const columns = [
    { name: 'name', title: labels.name, DataCell: ({ rowData }) => <span>{renderText(rowData.name)}</span> },
    { name: 'description', title: labels.description, DataCell: ({ rowData }) => <span>{renderText(rowData.description)}</span> },
    { name: 'value', title: labels.value, DataCell: ({ rowData }) => <span>{renderText(rowData.value)}</span> },
    { name: 'unit_of_measure', title: labels.unitOfMeasure, DataCell: ({ rowData }) => <span>{renderText(rowData.unit_of_measure)}</span> },
    { name: 'classification_header', title: labels.classificationHeader, DataCell: ({ rowData }) => <span>{renderText(rowData.classification_header)}</span> },
  ];
  return <PatchedTable columns={columns} data={rows} getRowKey={(_, i) => i} />;
};

const ComponentsTable = ({ rows, labels }) => {
  const columns = [
    { name: 'name', title: labels.name, DataCell: ({ rowData }) => <span>{renderText(rowData.name)}</span> },
    { name: 'description', title: labels.description, DataCell: ({ rowData }) => <span>{renderText(rowData.description)}</span> },
    { name: 'version', title: labels.version, DataCell: ({ rowData }) => <span>{renderText(rowData.version)}</span> },
  ];
  return <PatchedTable columns={columns} data={rows} getRowKey={(_, i) => i} />;
};

const LocationPanel = ({ location, labels }) => (
  <SummaryGrid>
    <div><Label>{labels.latitude}</Label><Value>{renderText(location?.latitude)}</Value></div>
    <div><Label>{labels.longitude}</Label><Value>{renderText(location?.longitude)}</Value></div>
    <div><Label>{labels.altitude}</Label><Value>{renderText(location?.altitude)}</Value></div>
    <div><Label>{labels.heading}</Label><Value>{renderText(location?.heading)}</Value></div>
  </SummaryGrid>
);

const Show = ({ record, labels = {} }) => {
  const tabsConfig = [
    {
      id: 'faults',
      title: `${labels.faults} (${record.faults?.length ?? 0})`,
      body: <FaultsTable faults={record.faults} labels={labels} />,
    },
  ];

  if (record.sensor_readings?.length) {
    tabsConfig.push({
      id: 'sensor_readings',
      title: labels.sensorReadings,
      body: <SensorReadingsTable rows={record.sensor_readings} labels={labels} />,
    });
  }
  if (record.location) {
    tabsConfig.push({
      id: 'location',
      title: labels.location,
      body: <LocationPanel location={record.location} labels={labels} />,
    });
  }
  if (record.components?.length) {
    tabsConfig.push({
      id: 'components',
      title: labels.components,
      body: <ComponentsTable rows={record.components} labels={labels} />,
    });
  }

  return (
    <AdminPage
      title={labels.readingDetail}
      breadcrumbs={[
        { text: labels.diagnosticReadings, href: ENDPOINT },
        { text: labels.readingDetail },
      ]}
    >
      <AdminPanel title={labels.readingDetail}>
        <SummaryGrid>
          {SUMMARY_FIELDS(labels).map(([label, key]) => (
            <div key={key}>
              <Label>{label}</Label>
              <Value>{renderText(record[key])}</Value>
            </div>
          ))}
        </SummaryGrid>
      </AdminPanel>

      <div style={{ marginTop: 16 }}>
        <Tabs defaultSelectedId={tabsConfig[0].id}>
          {tabsConfig.map((tab) => (
            <Tab key={tab.id} id={tab.id} title={tab.title}>
              <div style={{ marginTop: 16 }}>{tab.body}</div>
            </Tab>
          ))}
        </Tabs>
      </div>
    </AdminPage>
  );
};

export default Show;
